import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { CONNECT_STATUS, useSIPProvider } from "react-sipjs";

// Lucide Icons
import { 
  Menu, 
  Sun, 
  Moon, 
  Phone, 
  PhoneCall, 
  User, 
  CreditCard, 
  LogOut, 
  ChevronDown, 
  Wifi, 
  WifiOff 
} from "lucide-react";

// Local Imports
import { toggleSidebar } from "../slices/sidebarSlice";
import { BASE_API, DecryptToken } from "../helper/Constants";
import Tooltip from "../components/ui/Tooltip";
import OverlayTrigger from "../components/ui/OverlayTrigger";

const Header = ({ navHandler }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch(); // Kept if you plan to use it later, though navHandler is passed as prop
  const dropdownRef = useRef(null);
  
  // SIP Context
  const { connectStatus } = useSIPProvider();
  
  // Constants
  const API_SERVER_URL = window.CAMPAIN_WWS_URL || BASE_API;

  /* ---------------- State ---------------- */
  const [userEmail, setUserEmail] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const [callCounts, setCallCounts] = useState({
    campaignCallsCount: 0,
    extensionCallsCount: 0,
  });

  // Dark Mode State with Persistence (Checks local storage first)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") === "dark";
    }
    return false;
  });

  /* ---------------- Effects ---------------- */

  // 1. Handle Dark Mode Toggle & Persistence
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  // 2. Click Outside Dropdown to Close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 3. SSE for Live Calls
  useEffect(() => {
    if (!API_SERVER_URL) return;

    const eventSource = new EventSource(`${API_SERVER_URL}/api/stream/admin`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "update") {
          setCallCounts(data.data.counts);
        }
      } catch (err) {
        console.error("Error parsing SSE data:", err);
      }
    };

    eventSource.onerror = (err) => {
      console.warn("SSE Error:", err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [API_SERVER_URL]);

  // 4. Fetch User Info
  useEffect(() => {
    const fetchUserEmail = () => {
      try {
        const userToken = localStorage.getItem("psx_token");
        if (userToken) {
          const decodedToken = DecryptToken(userToken);
          if (decodedToken) {
            setUserEmail(decodedToken.email);
            // setUserId(decodedToken.user_id); // Uncomment if used later
          }
        }
      } catch (error) {
        console.error("Error fetching user email:", error);
      }
    };
    fetchUserEmail();
  }, []);

  /* ---------------- Handlers ---------------- */
  
  const handleLogout = () => {
    const userToken = localStorage.getItem("psx_token");
    if (userToken) {
      localStorage.removeItem("psx_token");
      navigate("/", { replace: true });
      toast.success("Logged out successfully");
    }
  };

  const renderTooltip = (props, text) => (
    <Tooltip id="button-tooltip" {...props}>
      {text}
    </Tooltip>
  );

  return (
    <header className="fixed top-0 z-50 w-full h-[64px] bg-white/90 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 shadow-sm transition-all duration-300 flex items-center px-4 md:px-6 justify-between">
      
      {/* --- Left Section: Logo & Toggle --- */}
      <div className="flex items-center gap-4">
        {/* Mobile Sidebar Toggle */}
        <button 
          onClick={navHandler}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          aria-label="Toggle Sidebar"
        >
          <Menu size={24} />
        </button>

        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2 group decoration-transparent">
          <div className="flex flex-col leading-none">
            <span className="text-xl font-extrabold tracking-tight">
              <span className="text-orange-500">CALL</span>
              <span className="text-blue-600 dark:text-blue-400">Adda</span>
            </span>
          </div>
        </Link>

        {/* SIP Status Badge (Desktop) */}
        <div className={`hidden md:flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border transition-colors duration-300 ${
          connectStatus === CONNECT_STATUS.CONNECTED
            ? "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-400"
            : "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-400"
        }`}>
          {connectStatus === CONNECT_STATUS.CONNECTED ? (
            <Wifi size={14} className="animate-pulse" />
          ) : (
            <WifiOff size={14} />
          )}
          <span>{connectStatus === CONNECT_STATUS.CONNECTED ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>

      {/* --- Right Section: Stats, Theme, Profile --- */}
      <div className="flex items-center gap-3 md:gap-5">
        
        {/* 1. Live Call Stats (Hidden on Mobile) */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Campaign Calls */}
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={(props) => renderTooltip(props, "Active Campaign Calls")}
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
              <PhoneCall size={16} className="text-green-600 dark:text-green-400" />
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                {callCounts.campaignCallsCount} <span className="text-xs font-medium text-slate-500">Camp</span>
              </span>
            </div>
          </OverlayTrigger>

          {/* Extension Calls */}
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={(props) => renderTooltip(props, "Active Extension Calls")}
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
              <Phone size={16} className="text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                {callCounts.extensionCallsCount} <span className="text-xs font-medium text-slate-500">Ext</span>
              </span>
            </div>
          </OverlayTrigger>
        </div>

        {/* 2. Theme Toggle */}
        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-all focus:outline-none"
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <Sun size={20} className="text-amber-400" />
          ) : (
            <Moon size={20} className="text-slate-600" />
          )}
        </button>

        {/* 3. Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-all focus:outline-none"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md ring-2 ring-white dark:ring-slate-800">
              {userEmail ? userEmail.charAt(0).toUpperCase() : <User size={18} />}
            </div>
            
            <div className="hidden md:flex flex-col items-start">
               {userEmail && (
                <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                   {userEmail.split('@')[0]}
                </span>
               )}
            </div>
            <ChevronDown 
              size={16} 
              className={`text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-3 w-60 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 py-2 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
              
              {/* Header */}
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                <p className="text-sm font-bold text-slate-800 dark:text-white truncate">
                  {userEmail || "Guest User"}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  Administrator
                </p>
              </div>

              {/* Menu Items */}
              <ul className="py-2">
                <li>
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <User size={18} />
                    My Profile
                  </Link>
                </li>
                <li>
                  <Link
                    to="/purchaseplan"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <CreditCard size={18} />
                    Purchase Plan
                  </Link>
                </li>
                <li className="my-1 border-t border-slate-100 dark:border-slate-700" />
                <li>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut size={18} />
                    Sign Out
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;